import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AppShell from "@/components/platform/AppShell";
import { api } from "@/lib/api";
import { useBreachMeta } from "@/lib/incidents";
import { Overview, Timeline, WsHeaderActions } from "@/components/breach/WsCore";
import { AffectedPeople, Scope, Vendors } from "@/components/breach/WsScope";
import { Decisions, TasksActions } from "@/components/breach/WsResponse";
import { BoardReports, Communications } from "@/components/breach/WsDocs";
import { Closure, ControlsDefs, Evidence, Lessons, RootCause } from "@/components/breach/WsClose";

const TABS = [["overview", "Overview", Overview], ["timeline", "Timeline", Timeline], ["scope", "Scope and impact", Scope], ["tasks", "Tasks and actions", TasksActions],
  ["vendors", "Vendors", Vendors], ["decisions", "Decisions", Decisions], ["people", "Affected people", AffectedPeople], ["communications", "Communications", Communications],
  ["reports", "Board reports", BoardReports], ["evidence", "Evidence", Evidence], ["controls", "Controls and deficiencies", ControlsDefs], ["rootcause", "Root cause", RootCause],
  ["lessons", "Lessons learned", Lessons], ["closure", "Closure", Closure], ["audit", "Audit history", (p) => <Timeline {...p} audit />]];

export default function IncidentWorkspace() {
  const { iid } = useParams();
  const meta = useBreachMeta();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const [inc, setInc] = useState(null);
  const load = useCallback(() => api.get(`/incidents/${iid}`).then(({ data }) => setInc(data)).catch(() => setInc(false)), [iid]);
  useEffect(() => { load(); }, [load]);
  if (inc === false) return <AppShell testId="incident-workspace"><div className="text-sm text-slate-500">Incident not found.</div></AppShell>;
  if (!inc || !meta) return <AppShell testId="incident-workspace"><div className="text-sm text-slate-500">Loading...</div></AppShell>;
  return (
    <AppShell testId="incident-workspace">
      <Link to="/breach?tab=register" className="text-xs text-slate-500 flex items-center gap-1" data-testid="back-to-register"><ArrowLeft className="w-3 h-3" />Breach Desk</Link>
      <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
        <div><div className="font-mono text-xs text-slate-500">{inc.incident_reference} · {inc.classification_label}</div>
          <h1 className="text-2xl font-bold text-[#003366]" data-testid="incident-title">{inc.incident_title}</h1></div>
        <WsHeaderActions inc={inc} meta={meta} reload={load} />
      </div>
      {inc.warnings.length > 0 && <div className="mt-3 space-y-1" data-testid="incident-warnings">{inc.warnings.map((w) => (
        <div key={w} className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2 flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0" />{w}</div>))}</div>}
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })} className="mt-5">
        <TabsList className="bg-white border border-slate-200 flex-wrap h-auto" data-testid="incident-tabs">
          {TABS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`itab-${k}`}>{l}</TabsTrigger>)}
        </TabsList>
        {TABS.map(([k, , C]) => <TabsContent key={k} value={k} className="mt-5">{tab === k && <C inc={inc} meta={meta} reload={load} />}</TabsContent>)}
      </Tabs>
    </AppShell>
  );
}
